import React, {ComponentProps} from "react";
import {useTranslation} from "react-i18next";
import {useTour} from "../../../providers/tour";

import Table from "../../../ui/table";
import Skeleton from "../../../ui/skeleton";
import ChallengesTableTable, {ChallengeParams} from "./ChallengesTable.table";
import {classes as tourClasses, isEditorIndexTourMock} from "../../../scenes/editor/index.tour";
import {Draggable, Droppable} from "react-beautiful-dnd";
import {DragIndicatorIcon} from "../../../ui/icons";

import cn from "classnames";
import classes from "./ChallengesTable.module.css";
import {ProjectParams} from "../challenges-table/ChallengesTable.table";
import {isEditorProjectEditTourMock} from "../../../scenes/editor/project-edit.tour";

type GenericProps = Pick<ComponentProps<typeof Table>, "className"> & {
  allowDragNDrop?: boolean;
};

export type ChallengeProps = GenericProps & ChallengeParams;
export type ProjectProps = GenericProps & ProjectParams;

export type Props = (ChallengeProps | ProjectProps) & {
  manageDisabled?: boolean;
  onSubscriptionModalOpen?: () => void;
};

export default function ChallengesTable<P extends Props>({
  className,
  data,
  projects,
  currentProject,
  onChallengeCopy,
  onChallengeDelete,
  onChallengeMove,
  onProjectCopy,
  onProjectDelete,
  allowDragNDrop,
  manageDisabled,
  onSubscriptionModalOpen
}: P) {
  const {t} = useTranslation();

  const {getTableProps, getTableBodyProps, headerGroups, rows, prepareRow} = ChallengesTableTable.use(
    data,
    projects,
    currentProject,
    manageDisabled || false,
    onChallengeCopy,
    onChallengeDelete,
    onChallengeMove,
    onProjectCopy,
    onProjectDelete,
    onSubscriptionModalOpen
  );

  const {mock} = useTour();

  return (
    <Table className={cn(classes.root, className)} {...getTableProps()}>
      <Table.Head className={tourClasses.challengeTableHeader}>
        {headerGroups.map((headerGroup) => (
          <Table.Row {...headerGroup.getHeaderGroupProps()}>
            {allowDragNDrop && <Table.Cell />}
            {headerGroup.headers.map((column) => (
              <Table.Cell {...column.getHeaderProps()}>{column.render("Header")}</Table.Cell>
            ))}
          </Table.Row>
        ))}
      </Table.Head>
      <DroppableTableBody
        {...(allowDragNDrop ? {asDroppable: true, droppableId: "challengeTable"} : {asDroppable: false})}
        {...getTableBodyProps()}
      >
        {rows.map((row, index) => {
          prepareRow(row);

          const type = row.original.__typename?.toLowerCase() as "challenge" | "project";
          const indexItemMock = isEditorIndexTourMock(mock) ? mock?.getBy[type]?.get(row.original.id) : undefined;
          const projectItemMock = (isEditorProjectEditTourMock(mock) && type === "challenge")
            ? mock?.getBy[type]?.get(row.original.id) : undefined;

          const draggableProps: ComponentProps<typeof DraggableTableRow> = allowDragNDrop
            ? {
                asDraggable: true,
                draggableId: `${type}-${row.original.id}`,
                index,
              }
            : {
                asDraggable: false,
              };

          return (
            <DraggableTableRow
              {...draggableProps}
              className={cn(indexItemMock?.classes.row, classes.row)}
              {...row.getRowProps()}
            >
              {allowDragNDrop && (
                <Table.Cell className={cn(classes.dragIndicatorCell, projectItemMock?.classes.dragHandle)}>
                  <DragIndicatorIcon className={classes.dragIndicator} />
                </Table.Cell>
              )}
              {row.cells.map((cell) => (
                <Table.Cell {...cell.getCellProps()}>{cell.render("Cell")}</Table.Cell>
              ))}
            </DraggableTableRow>
          );
        })}
        {!rows.length && (
          <Table.Row>
            <Table.Cell colSpan={999}>
              <div className={classes.noData}>{t("components.ChallengesTable.noData")}</div>
            </Table.Cell>
          </Table.Row>
        )}
      </DroppableTableBody>
    </Table>
  );
}

function DroppableTableBody(
  props: Omit<ComponentProps<typeof Table.Body>, "innerRef"> &
    (
      | {
          asDroppable?: false;
        }
      | ({
          asDroppable: true;
        } & Omit<ComponentProps<typeof Droppable>, "children">)
    ),
) {
  const {asDroppable, ...rest} = props;

  if (asDroppable) {
    const {
      droppableId,
      type,
      mode,
      isDropDisabled,
      isCombineEnabled,
      direction,
      ignoreContainerClipping,
      renderClone,
      getContainerForClone,
      children,
      asDroppable,
      ...rest
    } = props;

    const droppableProps = {
      droppableId,
      type,
      mode,
      isDropDisabled,
      isCombineEnabled,
      direction,
      ignoreContainerClipping,
      renderClone,
      getContainerForClone,
    };
    return (
      <Droppable {...droppableProps}>
        {(provided) => (
          <Table.Body {...rest} innerRef={provided.innerRef}>
            {children}
            {provided.placeholder}
          </Table.Body>
        )}
      </Droppable>
    );
  }

  return <Table.Body {...rest} />;
}

function DraggableTableRow(
  props: Omit<ComponentProps<typeof Table.Row>, "innerRef"> &
    ({asDraggable?: false} | ({asDraggable: true} & Omit<ComponentProps<typeof Draggable>, "children">)),
) {
  const {asDraggable, ...rest} = props;

  if (asDraggable) {
    const {
      draggableId,
      index,
      disableInteractiveElementBlocking,
      isDragDisabled,
      shouldRespectForcePress,
      asDraggable,
      ...rest
    } = props;
    const draggableProps = {
      draggableId,
      index,
      disableInteractiveElementBlocking,
      isDragDisabled,
      shouldRespectForcePress,
    };
    return (
      <Draggable {...draggableProps} key={draggableId}>
        {(provided) => (
          <Table.Row
            {...rest}
            innerRef={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          />
        )}
      </Draggable>
    );
  }

  return <Table.Row {...rest} />;
}

ChallengesTable.Skeleton = React.memo(function (props) {
  return (
    <Table className={classes.root} {...props}>
      <Table.Body>
        <ChallengesTable.SkeletonRow />
        <ChallengesTable.SkeletonRow />
        <ChallengesTable.SkeletonRow />
        <ChallengesTable.SkeletonRow />
      </Table.Body>
    </Table>
  );
});

ChallengesTable.SkeletonRow = React.memo(function () {
  return (
    <Table.Row>
      <Table.Cell>
        <Skeleton.Block width={280} height={28} />
      </Table.Cell>
      <Table.Cell>
        <Skeleton.Block width={28} height={28} />
      </Table.Cell>
      <Table.Cell>
        <Skeleton.Block width={50} height={28} />
      </Table.Cell>
      <Table.Cell>
        <Skeleton.Block width={28} height={28} />
      </Table.Cell>
    </Table.Row>
  );
});
